import { api_ } from '@shared/plugIn/axios';
import { LowerRole } from '@shared/types';

import { GiftStoreRole } from '../giftOrder/giftOrder.interface';
import {
  DeliveryChannel,
  GiftCoupon,
  GiftCouponForPublic,
  GiftCouponForStore,
  GiftCouponRedeemStatus,
  GiftCouponRequestDto,
  GiftCouponSaveRecipientDto,
  GiftCouponShareStatus,
  GiftCouponStatistics,
  GiftCouponSummary,
  ShareChannel,
  VerificationConfirmDto,
  VerificationConfirmResponse,
  VerificationSendDto,
  VerificationSendResponse,
} from './giftCoupon.interface';

// 쿠폰 단건 조회 (Public) — 카카오 공유 후 shareStatus / kakaoSharedAt 가벼운 폴링용
export const getGiftCouponById = async (id: string): Promise<GiftCouponForPublic> => {
  const { data } = await api_.get<GiftCouponForPublic>(
    `/${LowerRole.Public}/gift-coupon/${id}`,
  );
  return data;
};

/**
 * 공유 처리 (ShareStatus: NotShared → Shared)
 *
 * shareChannel을 함께 보내면 백엔드가 lastSharedChannel을 갱신하고
 * shareHistory에도 기록합니다. 카카오톡은 웹훅으로도 자동 보정됩니다.
 */
export const shareCoupon = async (
  id: string,
  role: GiftStoreRole,
  shareChannel?: ShareChannel,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(
    `/${role}/gift-coupon/share/${id}`,
    shareChannel ? { shareChannel } : {},
  );
  return response.data;
};

// 공유 취소 (ShareStatus: Shared → NotShared)
export const unshareCoupon = async (
  id: string,
  role: GiftStoreRole,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(
    `/${role}/gift-coupon/unshare/${id}`,
  );
  return response.data;
};

// 전달 확인 (confirmedAt 기록 + memo optional)
export const confirmCoupon = async (
  id: string,
  role: GiftStoreRole,
  memo?: string,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(
    `/${role}/gift-coupon/confirm-share/${id}`,
    memo ? { memo } : {},
  );
  return response.data;
};

// 조회 처리 (ShareStatus: Shared → Viewed)
export const viewCoupon = async (
  id: string,
  role: GiftStoreRole = LowerRole.Public,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(`/${role}/gift-coupon/view/${id}`);
  return response.data;
};

// 교환 신청 (RedeemStatus: Available → Requested)
export const requestCouponRedemption = async (
  id: string,
  dto: GiftCouponRequestDto,
  role: GiftStoreRole = LowerRole.Public,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(
    `/${role}/gift-coupon/request/${id}`,
    dto,
  );
  return response.data;
};

// 나중에 신청하기 — 수신자 번호만 미리 저장
export const saveRecipient = async (
  id: string,
  dto: GiftCouponSaveRecipientDto,
  role: GiftStoreRole = LowerRole.Public,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(
    `/${role}/gift-coupon/save-recipient/${id}`,
    dto,
  );
  return response.data;
};

// 발송 처리 (RedeemStatus: Requested → Delivered)
export interface DeliverCouponDto {
  priceWhole: number;
  priceRetail: number;
  priceRebate: number;
  /** 미선택 시 빈 문자열로 전달 */
  deliveryChannel: DeliveryChannel | '';
}

export const deliverCoupons = async (
  couponIds: string[],
  dto: DeliverCouponDto,
): Promise<GiftCouponForStore[]> => {
  const response = await api_.post<GiftCouponForStore[]>(
    `/${LowerRole.Admin}/gift-coupon/deliver`,
    {
      couponIds,
      priceWhole: dto.priceWhole,
      priceRetail: dto.priceRetail,
      priceRebate: dto.priceRebate,
      deliveryChannel: dto.deliveryChannel,
    },
  );
  return response.data;
};

export const deliverCoupon = async (
  id: string,
  dto: DeliverCouponDto,
): Promise<GiftCouponForStore> => {
  const results = await deliverCoupons([id], dto);
  return results[0] as GiftCouponForStore;
};

/**
 * 대리발송(신청 생략 즉시발송) — 관리자가 연락처를 받아 수령자 신청(번호인증·서명) 없이
 * 대리신청 + 발송완료를 한 번에 처리. Available 쿠폰은 phoneNumber 필수,
 * Requested 쿠폰은 생략(기존 recipient 유지). 가격·매체는 배치 공통이므로
 * 같은 giftItem 단위로만 묶어 호출할 것.
 * 스펙: docs/gift-coupon-proxy-deliver-spec.md · 백엔드 구현 완료(2026-07-14),
 * 404 가드는 구서버 안전장치로 유지.
 */
export interface ProxyDeliverEntry {
  couponId: string;
  /** 숫자만 (01012345678). Available 쿠폰이면 필수 */
  phoneNumber?: string;
  /**
   * 선택형 상품일 때 실제 발송한 choiceUnit _id — Available 쿠폰의 selectedChoiceUnit에
   * 기록해 "무슨 상품을 대리발송했는지" 남긴다. 백엔드 반영 완료(스펙 §7, 2026-07-14).
   * 구서버는 이 필드를 무시(발송 정상, 기록만 누락)라 하위호환 안전.
   */
  selectedChoiceUnit?: string;
}

export interface ProxyDeliverDto {
  entries: ProxyDeliverEntry[];
  priceWhole: number;
  priceRetail: number;
  priceRebate: number;
  deliveryChannel: DeliveryChannel;
}

export interface ProxyDeliverResult {
  deliveredCount: number;
  coupons?: GiftCouponForStore[];
}

export const proxyDeliverCoupons = async (
  dto: ProxyDeliverDto,
): Promise<ProxyDeliverResult> => {
  const { data } = await api_.post<ProxyDeliverResult>(
    `/${LowerRole.Admin}/gift-coupon/proxy-deliver`,
    dto,
  );
  return data;
};

// 발송불가 처리 (RedeemStatus: Requested → Rejected)
export const rejectCoupon = async (id: string): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    redeemStatus: GiftCouponRedeemStatus.Rejected,
  });
  return response.data;
};

// 수신자 전화번호 수정 (Admin)
export const updateRecipientPhone = async (
  id: string,
  phoneNumber: string,
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    'recipient.phoneNumber': phoneNumber,
  });
  return response.data;
};

// Admin에서 공유 상태를 NotShared + 교환 상태를 Available로 초기화
export const resetCouponToNotShared = async (id: string): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    shareStatus: GiftCouponShareStatus.NotShared,
    redeemStatus: GiftCouponRedeemStatus.Available,
  });
  return response.data;
};

// 쿠폰 통계 조회 (Admin)
export const fetchGiftCouponStatistics = async (): Promise<GiftCouponStatistics> => {
  const response = await api_.get<GiftCouponStatistics>(
    `/${LowerRole.Admin}/gift-coupon/statistics`,
  );
  return response.data;
};

// 재발행 쿠폰 생성 (Admin)
export interface CreateAdditionalCouponsDto {
  giftOrderId: string;
  count: number;
  giftItemId?: string;
}

export const createAdditionalCoupons = async (
  dto: CreateAdditionalCouponsDto,
): Promise<GiftCouponForStore[]> => {
  const response = await api_.post<GiftCouponForStore[]>(
    `/${LowerRole.Admin}/gift-coupon/additional`,
    dto,
  );
  return response.data;
};

// 재발행 쿠폰 폐기 (Admin) - DB에서 완전 삭제
export const deleteAdditionalCoupon = async (id: string): Promise<void> => {
  await api_.delete(`/${LowerRole.Admin}/gift-coupon/additional/${id}`);
};

// 재발행 쿠폰 수신자 전화번호 저장 (Admin - 기존 patch 방식)
export const updateCouponAdditionalRecipientPhone = async (
  id: string,
  additionalRecipientPhone: string,
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    additionalRecipientPhone,
  });
  return response.data;
};

// 확인번호 + 메모 함께 업데이트 (Admin)
export const updateCouponPhoneAndMemo = async (
  id: string,
  data: { additionalRecipientPhone?: string; memo?: string },
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, data);
  return response.data;
};

// 재발행 쿠폰 수신자 정보 변경 (Store - 전용 엔드포인트)
export const updateAdditionalRecipient = async (
  id: string,
  phoneNumber: string,
  role: GiftStoreRole,
  memo?: string,
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(
    `/${role}/gift-coupon/additional-recipient/${id}`,
    { phoneNumber, ...(memo ? { memo } : {}) },
  );
  return response.data;
};

/**
 * 카카오톡 회수(잘못 보낸) 케이스에서 일반 쿠폰의 수령자 번호를 잠그기 위한 엔드포인트.
 * 백엔드는 `additionalRecipientPhone` 필드를 업데이트하며 (재발행 쿠폰의
 * `additional-recipient` 와 동일하게 `memo` 동봉 시 메모도 함께 저장), 이후 redeem 시
 * 이 번호로만 본인 인증을 허용해 다른 수신자가 받지 못하도록 막는다.
 */
export const updateCouponRestrictedPhone = async (
  id: string,
  phoneNumber: string,
  role: GiftStoreRole,
  memo?: string,
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(
    `/${role}/gift-coupon/restricted-phone/${id}`,
    { phoneNumber, ...(memo ? { memo } : {}) },
  );
  return response.data;
};

// 쿠폰 폐기만 (재발행 없이) (Admin)
export interface DisposeCouponsDto {
  couponIds: string[];
  reason?: string;
}

export const disposeCoupons = async (dto: DisposeCouponsDto): Promise<GiftCoupon[]> => {
  const response = await api_.post<GiftCoupon[]>(
    `/${LowerRole.Admin}/gift-coupon/dispose`,
    dto,
  );
  return response.data;
};

// 쿠폰 폐기 + 재발급 (Admin)
export interface DisposeAndReissueCouponsDto {
  couponIds: string[];
  reason?: string;
}

export interface DisposeAndReissueResult {
  disposed: GiftCoupon[];
  reissued: GiftCoupon[];
}

export const disposeAndReissueCoupons = async (
  dto: DisposeAndReissueCouponsDto,
): Promise<DisposeAndReissueResult> => {
  const response = await api_.post<DisposeAndReissueResult>(
    `/${LowerRole.Admin}/gift-coupon/dispose-and-reissue`,
    dto,
  );
  return response.data;
};

// additional → reissued 승격 (Admin)
export const promoteToReissued = async (id: string): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(
    `/${LowerRole.Admin}/gift-coupon/${id}/promote-to-reissued`,
  );
  return response.data;
};

// 발송매체(deliveryChannel)만 수정 (Admin)
export const updateCouponDeliveryChannel = async (
  id: string,
  deliveryChannel: DeliveryChannel | '',
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    'gifticon.deliveryChannel': deliveryChannel,
  });
  return response.data;
};

// 발송 정보(도매가/매체 등) 수정 (Admin) - 전달된 필드만 patch
export interface UpdateDeliveredCouponFieldsDto {
  /** 미지정(undefined)이면 변경 안함, 빈 문자열이면 매체 비움 */
  deliveryChannel?: DeliveryChannel | '';
  /** 미지정(undefined)이면 변경 안함 */
  priceWhole?: number;
}

export const updateDeliveredCouponFields = async (
  id: string,
  dto: UpdateDeliveredCouponFieldsDto,
): Promise<GiftCouponForStore> => {
  const body: Record<string, unknown> = {};
  if (dto.deliveryChannel !== undefined) {
    body['gifticon.deliveryChannel'] = dto.deliveryChannel;
  }
  if (dto.priceWhole !== undefined) {
    body['gifticon.priceWhole'] = dto.priceWhole;
  }
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, body);
  return response.data;
};

// 발송일시(deliveredAt)만 수정 (Admin)
export const updateCouponDeliveredAt = async (
  id: string,
  deliveredAt: string,
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    'gifticon.deliveredAt': deliveredAt,
  });
  return response.data;
};

// 일반 환불 (미발송 쿠폰)
export interface RefundCouponDto {
  couponId: string;
  amount: number;
}

export const refundCoupon = async (dto: RefundCouponDto): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(`/admin/gift-coupon/refund`, dto);
  return response.data;
};

// 발송 후 환불 (이미 발송된 쿠폰)
export interface RefundAfterDeliveryDto {
  couponId: string;
  amount: number;
}

export const refundAfterDelivery = async (
  dto: RefundAfterDeliveryDto,
): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(
    `/admin/gift-coupon/refund-after-delivery`,
    dto,
  );
  return response.data;
};

// 환불/연장 Issue 발행 (Admin → 고객에게 신청 링크 허용)
export interface IssueCouponDto {
  couponId: string;
  type: 'refund' | 'renewal';
}

export const issueCoupon = async (dto: IssueCouponDto): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(`/admin/gift-coupon/issue`, dto);
  return response.data;
};

// Issue 취소 (Admin)
export const cancelIssueCoupon = async (id: string): Promise<GiftCoupon> => {
  const response = await api_.delete<GiftCoupon>(`/admin/gift-coupon/issue/${id}`);
  return response.data;
};

// Issued 목록 조회 (Admin)
export const findIssuedCoupons = async (): Promise<GiftCoupon[]> => {
  const response = await api_.get<GiftCoupon[]>(`/admin/gift-coupon/issued`);
  return response.data;
};

// 환불 승인 (Admin)
export interface ApproveRefundDto {
  couponId: string;
  amount: number;
}

export const approveRefund = async (dto: ApproveRefundDto): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(`/admin/gift-coupon/approve-refund`, dto);
  return response.data;
};

// 연장 승인 (Admin)
export interface ApproveRenewalDto {
  couponId: string;
  priceWhole: number;
  priceRetail: number;
  priceRebate: number;
  deliveryChannel: string;
}

export const approveRenewal = async (dto: ApproveRenewalDto): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(`/admin/gift-coupon/approve-renewal`, dto);
  return response.data;
};

// Public: 환불 신청 (고객이 링크에서 작성)
export interface RefundRequestDto {
  phoneNumber: string;
  name: string;
  bank: string;
  accountNumber: string;
  accountHolder: string;
  signatureImage: string;
}

export const requestRefund = async (
  couponId: string,
  dto: RefundRequestDto,
): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(
    `/${LowerRole.Public}/gift-coupon/refund-request/${couponId}`,
    dto,
  );
  return response.data;
};

// 연장쿠폰 생성
export interface CreateRenewalCouponDto {
  originalCouponId: string;
  recipientPhoneNumber?: string;
}

export const createRenewalCoupon = async (
  dto: CreateRenewalCouponDto,
): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(`/admin/gift-coupon/renewal`, dto);
  return response.data;
};

// 연장쿠폰 삭제
export const deleteRenewalCoupon = async (id: string): Promise<void> => {
  await api_.delete(`/admin/gift-coupon/renewal/${id}`);
};

// Public: 연장쿠폰 신청 (고객이 링크에서 작성)
export interface RenewalRequestDto {
  phoneNumber: string;
  name: string;
  signatureImage: string;
}

export const requestRenewal = async (
  couponId: string,
  dto: RenewalRequestDto,
): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(
    `/${LowerRole.Public}/gift-coupon/renewal-request/${couponId}`,
    dto,
  );
  return response.data;
};

// 번호 인증 코드 발송
export const sendVerificationCode = async (
  dto: VerificationSendDto,
): Promise<VerificationSendResponse> => {
  const response = await api_.post<VerificationSendResponse>(
    `/${LowerRole.Public}/gift-coupon/verification/send`,
    dto,
  );
  return response.data;
};

// 번호 인증 코드 확인
export const confirmVerificationCode = async (
  dto: VerificationConfirmDto,
): Promise<VerificationConfirmResponse> => {
  const response = await api_.post<VerificationConfirmResponse>(
    `/${LowerRole.Public}/gift-coupon/verification/confirm`,
    dto,
  );
  return response.data;
};

// ─── 추가교환 (중복 공유 쿠폰) ───

/**
 * 충당 가능 재고 조회 (Admin) — 추가교환 열기 전 판단용.
 * 같은 주문·상품의 미전달(Available) 쿠폰을 반환한다.
 * :id = 원본 쿠폰 _id
 */
export interface AvailableInventoryResponse {
  giftItem?: { _id: string; name?: string } | string;
  availableCount: number;
  coupons: GiftCouponSummary[];
}

export const getCouponAvailableInventory = async (
  id: string,
): Promise<AvailableInventoryResponse> => {
  const { data } = await api_.get<AvailableInventoryResponse>(
    `/${LowerRole.Admin}/gift-coupon/${id}/available-inventory`,
  );
  return data;
};

/**
 * 추가교환 열기 (Admin/GiftAgent) — :id = 원본 쿠폰 _id.
 * 재오픈 시 allowed만 조정, used 보존. allowed < used면 백엔드가 거부.
 */
export interface OpenExtraRedeemDto {
  allowed: number;
  reason?: string;
  expiresAt?: string; // ISO string · 미지정 시 무기한
}

export const openExtraRedeem = async (
  id: string,
  dto: OpenExtraRedeemDto,
): Promise<GiftCouponForStore> => {
  const { data } = await api_.post<GiftCouponForStore>(
    `/${LowerRole.Admin}/gift-coupon/${id}/extra-redeem/open`,
    dto,
  );
  return data;
};

/** 추가교환 닫기 (Admin/GiftAgent) — extraRedeem 제거 */
export const closeExtraRedeem = async (id: string): Promise<GiftCouponForStore> => {
  const { data } = await api_.patch<GiftCouponForStore>(
    `/${LowerRole.Admin}/gift-coupon/${id}/extra-redeem/close`,
  );
  return data;
};

/**
 * 수신자 추가신청 (Public) — :id = 원본 쿠폰 _id.
 * 번호인증이 원본 쿠폰 verification과 일치해야 하며, 스폰된 새 쿠폰(Requested)을 반환한다.
 */
export const requestExtraRedeem = async (
  id: string,
  dto: GiftCouponRequestDto,
): Promise<GiftCouponForStore> => {
  const { data } = await api_.post<GiftCouponForStore>(
    `/${LowerRole.Public}/gift-coupon/extra-redeem/${id}`,
    dto,
  );
  return data;
};

/**
 * 수신자 추가신청 — 나중에 받기 (Public) — :id = 원본 쿠폰 _id.
 * 본인 전용 spawn 쿠폰을 Available(예약) 상태로 생성해 반환한다.
 * 같은 주문·번호로 이미 Available spawn이 있으면 그것을 반환(멱등). 응답의 code로
 * 본인 쿠폰 URL을 만들어 이동/저장한다.
 */
export const saveExtraRedeemLater = async (
  id: string,
  dto: GiftCouponSaveRecipientDto,
): Promise<GiftCouponForStore> => {
  const { data } = await api_.post<GiftCouponForStore>(
    `/${LowerRole.Public}/gift-coupon/extra-redeem/${id}/save-later`,
    dto,
  );
  return data;
};

// ─── 추가결제 대기건 처리 (Admin) ───

/**
 * (A) 추가결제 후 발송 — :id = 스폰 쿠폰 _id.
 * extraPayment.isPaid=true 로 전환되어 발송대기로 이동.
 */
export interface ExtraPaymentDto {
  method?: string; // 예: "Card" | "BankTransfer"
  amount?: number;
  receiptId?: string;
}

export const payExtraPayment = async (
  id: string,
  dto: ExtraPaymentDto,
): Promise<GiftCouponForStore> => {
  const { data } = await api_.patch<GiftCouponForStore>(
    `/${LowerRole.Admin}/gift-coupon/${id}/extra-payment`,
    dto,
  );
  return data;
};

/**
 * (B) 대체발송 후보 조회 — :id = 스폰 쿠폰 _id.
 * 같은 주문·상품의 Available 쿠폰 배열 (seq 오름차순).
 */
export const getDonorCandidates = async (
  id: string,
): Promise<GiftCouponSummary[]> => {
  const { data } = await api_.get<GiftCouponSummary[]>(
    `/${LowerRole.Admin}/gift-coupon/${id}/donor-candidates`,
  );
  return data;
};

/**
 * (B) 기구매 미전달 쿠폰으로 대체발송 — :id = 스폰 쿠폰 _id.
 * 스폰 생존 + 상속 방식: 스폰이 donor의 seq·purchasedAt·expiresAt를 상속하고
 * settledBy='inventory'·absorbedDonor=donorId로 그대로 발송대기(Requested) 유지된다.
 * coupon: 생존한 스폰(=실제 발송될 단일 live 레코드).
 * disposed: 흡수되어 폐기된 donor (Disposed, 통계 제외).
 */
export interface FulfillFromExistingResult {
  coupon: GiftCoupon;
  disposed: GiftCoupon;
}

export const fulfillFromExisting = async (
  id: string,
  donorCouponId: string,
): Promise<FulfillFromExistingResult> => {
  const { data } = await api_.post<FulfillFromExistingResult>(
    `/${LowerRole.Admin}/gift-coupon/${id}/fulfill-from-existing`,
    { donorCouponId },
  );
  return data;
};

/**
 * (C) 추가결제 대기건 취소 (Admin) — :id = 스폰 쿠폰 _id.
 * 미정산·Requested·미결제 스폰 쿠폰만 취소 가능. 백엔드가 부수효과를 되돌리고
 * (흡수 donor 복원 / extraPayments 항목 제거 / 원본 extraRedeem.used -1) 스폰 쿠폰을
 * hard-delete 한다. 실제 결제된(amount>0) 건은 환불 흐름으로 따로 처리하므로 거부된다.
 */
export const cancelSpawnedCoupon = async (
  id: string,
): Promise<{ deleted: boolean }> => {
  const { data } = await api_.delete<{ deleted: boolean }>(
    `/${LowerRole.Admin}/gift-coupon/spawned/${id}`,
  );
  return data;
};

// ─── 답례 인사 일괄 발송 (Admin/GiftAgent) ───

/**
 * 선택한 쿠폰들의 additionalRecipientPhone(확인번호)로 답례글 LMS를 일괄 발송한다.
 * - 번호 없는 쿠폰은 서버가 skipped(NO_PHONE)로 건너뜀(에러 아님).
 * - 일부 실패해도 끝까지 시도 후 집계를 반환하며 HTTP는 2xx.
 * - 본문 하단에 "답례품 받기 {baseUrl}/gift-store/coupon/{couponCode}" 링크가 자동 부착된다.
 *   (couponIds(_id)로 조회한 각 쿠폰의 code로 서버가 링크를 생성)
 */
export interface SendCourtesyMessageDto {
  couponIds: string[];
  message: string;
  baseUrl: string;
}

export interface CourtesyMessageSkipped {
  couponId: string;
  /** NOT_FOUND(쿠폰 없음) | NO_PHONE(번호 없음) */
  reason: string;
}

export interface CourtesyMessageFailed {
  couponId: string;
  /** 발송 API 에러 메시지 */
  reason: string;
}

export interface SendCourtesyMessageResult {
  /** 중복 제거 후 처리 대상 수 */
  total: number;
  sentCount: number;
  skippedCount: number;
  failedCount: number;
  sent: string[];
  skipped: CourtesyMessageSkipped[];
  failed: CourtesyMessageFailed[];
}

export const sendCourtesyMessage = async (
  dto: SendCourtesyMessageDto,
): Promise<SendCourtesyMessageResult> => {
  const { data } = await api_.post<SendCourtesyMessageResult>(
    `/${LowerRole.Admin}/gift-coupon/send-courtesy-gift-message`,
    dto,
  );
  return data;
};

// ─── 이용안내(상품권 교환·잔액 유의사항 법적 고지) 일괄 발송 (Admin) ───

/**
 * giftOrder 단위로, 이미 교환(Requested/Delivered)했고 신청자(수신) 번호가 있는
 * 쿠폰들에 이용안내(교환·잔액 유의사항) LMS를 일괄 발송한다.
 * - 교환 전이거나 번호 없는 쿠폰은 서버가 skipped로 건너뜀(에러 아님).
 * - 일부 실패해도 끝까지 시도 후 집계를 반환(HTTP 2xx).
 * - 본문은 서버 기본 문구(DEFAULT_GUIDE_MESSAGE)를 사용하며, message로 덮어쓸 수 있다.
 * - excludePhones에 담긴 번호는 발송 제외(서버가 숫자만 비교 — 하이픈/공백 무관).
 *   엔드포인트: POST /admin/gift-order/:id/send-coupon-guide-message
 */
export interface SendUsageGuideDto {
  giftOrderId: string;
  /** 안내 본문 직접 지정 (미지정 시 서버 기본 이용안내 문구 사용) */
  message?: string;
  /** 발송 제외할 번호 목록 (숫자만/하이픈 포함 무관 — 서버가 숫자만 비교) */
  excludePhones?: string[];
}

export interface UsageGuideSkipped {
  couponId: string;
  /** NOT_FOUND(쿠폰 없음) | NO_PHONE(번호 없음) 등 */
  reason: string;
}

export interface UsageGuideFailed {
  couponId: string;
  /** 발송 API 에러 메시지 */
  reason: string;
}

export interface SendUsageGuideResult {
  /** 중복 제거 후 처리 대상 수 */
  total: number;
  sentCount: number;
  skippedCount: number;
  failedCount: number;
  sent: string[];
  skipped: UsageGuideSkipped[];
  failed: UsageGuideFailed[];
}

export const sendCouponUsageGuide = async (
  dto: SendUsageGuideDto,
): Promise<SendUsageGuideResult> => {
  const { giftOrderId, message, excludePhones } = dto;
  const body: Pick<SendUsageGuideDto, 'message' | 'excludePhones'> = {};
  if (message) body.message = message;
  if (excludePhones?.length) body.excludePhones = excludePhones;
  const { data } = await api_.post<SendUsageGuideResult>(
    `/${LowerRole.Admin}/gift-order/${giftOrderId}/send-coupon-guide-message`,
    body,
  );
  return data;
};
